import React, { useContext, useEffect, useRef, useState } from 'react';
import './styles.css';
import Panel from '../Panel';
import IconButton from '../IconButton';
import {
  Close as CloseIcon,
  AddMethod as MethodIcon,
  AddEvent as EventIcon,
  AddSpecialisedEvent as SpecialisedEventIcon,
  DeleteTool as DeleteToolIcon,
  SortRows as SortRowsIcon,
  SortColumns as SortColumnsIcon,
  SortColumnsBUL as SortColumnsBULIcon,
  SortColumnsTDL as SortColumnsTDLIcon,
  Picture as PictureIcon,
  CsvExport as CsvExportIcon,
} from '../Icons';

import Toolbar from '../Toolbar';
import { observer } from 'mobx-react';
import OETHeader from './OETHeader';
import OETBody from './OETBody';

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from '@dnd-kit/core';

import { toPng } from 'html-to-image';

import ImageContext, { type CaptureOET } from '../ImageContext';

import DndTypes from './DnDTypes';
import OETDragOverlay from './OETDragOverlay';
import OETDragContext, {
  canDropColumnOnCell,
  canDropColumnOnFirst,
  canDropRowOnHeader,
  canDropRowOnRow,
  type OETDragState,
} from './dndContext';
import { detectOETExportBrowser } from './browserSupport';

import { useStores } from '../../hooks/useStores';
import type { IModel } from '../../stores/model';
import type { IUIOET } from '../../stores/ui/oet';
import type { ToolbarButton } from '../Toolbar';
import type { MoveRow, MoveColumn } from './dndCallbacks';
import type { OETDragItem } from './DnDTypes';
import type { IOETRow } from '../../stores/model/uimodel/oetrow';
import type { IOETColumn } from '../../stores/model/uimodel/oetcolumn';

/**
 * Props for the inner {@link OET} component.
 *
 * - model: the MERODE model (may be null while the version content loads; the
 *   OET is only rendered with loaded content, so it is dereferenced below).
 * - ui: the OET UI store (active tool, dragged column, highlighted methods).
 * - selected: id of the currently selected element.
 * - isTouch: is this a touch device?
 * - setCapture: register the OET's image-capture function with the ImageContext.
 * - requestClose: close the OET view.
 * - onAddSpecialisedEvent / onAddMethod / onAddEvent / onDelete / onSelect:
 *   tool callbacks (see the toolbar).
 * - onExportCsv / onExportImage: export callbacks.
 */
export interface OETProps {
  model: IModel | null;
  ui: IUIOET;
  selected: string | null;
  isTouch?: boolean;
  setCapture?: (fn: CaptureOET) => void;
  requestClose: () => void;
  onAddSpecialisedEvent?: (eventType: string) => void;
  onAddMethod?: (eventType: string, objectType: string) => void;
  onSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  onAddEvent?: () => void;
  onExportCsv?: () => void;
  onExportImage?: () => void;
}

const OET = observer(function OET(props: OETProps) {
  const {
    model,
    ui,
    selected,
    isTouch,
    setCapture,
    requestClose,
    onAddSpecialisedEvent,
    onAddMethod,
    onSelect: onSelectProp,
    onDelete,
    onAddEvent,
    onExportCsv,
    onExportImage,
  } = props;

  const tableRef = useRef<HTMLDivElement | null>(null);
  const browser = detectOETExportBrowser();

  // The active drag item + the droppable currently under the pointer. Shared
  // with every drop target through OETDragContext so each re-derives its own
  // `isOver && canDrop` the way the react-dnd monitor did. The pointer sensor's
  // activation distance keeps a click from starting a drag (the HTML5 backend
  // only started a drag once the pointer actually moved).
  const [dragState, setDragState] = useState<OETDragState>({ activeItem: null, overId: null });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // captureImage must be reachable from the cDM effect (setCapture) without
  // re-subscribing; a ref keeps the latest closure available to the registered fn.
  const captureImageRef = useRef<CaptureOET>(undefined);
  captureImageRef.current = () => {
    if (tableRef.current) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          toPng(tableRef.current as HTMLElement)
            .then((result) => {
              resolve({ image: result });
            })
            .catch(reject);
        }, 50);
      });
    } else {
      return Promise.reject('OET is not ready');
    }
  };

  useEffect(() => {
    if (setCapture) setCapture((...args) => captureImageRef.current!(...args));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRowHeaderClick = (eventType: string) => {
    if (ui.activeTool === 'add_specialised_event' && onAddSpecialisedEvent) {
      onAddSpecialisedEvent(eventType);
    }
  };

  const onCellClick = (eventType: string, objectType: string) => {
    if (ui.activeTool === 'add_method' && onAddMethod) {
      onAddMethod(eventType, objectType);
    }
  };

  const onSelect = (id: string) => {
    if (ui.activeTool === 'delete' && onDelete) {
      onDelete(id);
    } else if (ui.activeTool === null && onSelectProp) {
      onSelectProp(id);
    }
  };

  const onSort = (key: string) => {
    const oetUiModel = model!.uimodel.oet;
    switch (key) {
      case 'sort_rows':
        oetUiModel.sortRows();
        break;
      case 'sort_columns':
        oetUiModel.sortColumns();
        break;
      case 'sort_columns_bul':
        oetUiModel.sortColumnsBUL();
        break;
      case 'sort_columns_tdl':
        oetUiModel.sortColumnsTDL();
        break;
      default:
        break;
    }
  };

  const exportCsv = () => {
    if (onExportCsv) onExportCsv();
  };

  const exportImage = () => {
    if (onExportImage) onExportImage();
  };

  const toggleButton = (
    key: Parameters<IUIOET['toggleTool']>[0],
    icon: unknown,
    title: string
  ): ToolbarButton => ({
    key,
    icon,
    handler: () => ui.toggleTool(key),
    active: ui.activeTool === key,
    title,
  });

  const sortButton = (key: string, icon: unknown, title: string): ToolbarButton => ({
    key,
    icon,
    handler: () => onSort(key),
    active: false,
    title,
  });

  const toolbarButtons = (): ToolbarButton[] => {
    const buttons: ToolbarButton[] = [
      toggleButton('add_method', MethodIcon, 'Add method'),
      {
        key: 'add_event',
        icon: EventIcon,
        handler: onAddEvent,
        active: false,
        title: 'Add event',
      },
      toggleButton('add_specialised_event', SpecialisedEventIcon, 'Add specialised event'),
      toggleButton('delete', DeleteToolIcon, 'Delete tool'),
      sortButton('sort_rows', SortRowsIcon, 'Sort rows'),
      sortButton('sort_columns', SortColumnsIcon, 'Sort columns'),
      sortButton('sort_columns_bul', SortColumnsBULIcon, 'Sort columns bottom-up'),
      sortButton('sort_columns_tdl', SortColumnsTDLIcon, 'Sort columns top-down'),
    ];
    if (browser === 'chrome' || browser === 'firefox') {
      buttons.push({
        key: 'export_image',
        icon: PictureIcon,
        handler: exportImage,
        active: false,
        title: 'Save image',
      });
    }
    buttons.push({
      key: 'export_csv',
      icon: CsvExportIcon,
      handler: exportCsv,
      active: false,
      title: 'Save as CSV',
    });
    return buttons;
  };

  const renderHeader = () => {
    return (
      <div className="oet-view-header h-full flex justify-center items-center pl-[15px]">
        <div className="title mr-[15px]" title="Object Event Table">
          OET
        </div>
        <Toolbar variant="panel" className="grow mr-[15px]" buttons={toolbarButtons()} />
        <div className="close-wrapper">
          <IconButton onClick={requestClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
    );
  };

  const moveRow: MoveRow = (from, to, group) => {
    const oet = model!.uimodel.oet;
    if (group === 'top') {
      oet.moveRow(from, to);
    } else {
      const row = oet.flattenedRows.find((r) => r.event.id === group);
      row!.moveSubRow(from, to);
    }
  };

  const moveColumn: MoveColumn = (from, to) => {
    model!.uimodel.oet.moveColumn(from, to);
  };

  // --- dnd-kit drag handlers ---------------------------------------------
  // onDragStart: record the active item; mark the dragged column on the ui
  // store (the body fades that column's cells, same as the old beginDrag).
  const onDragStart = (event: DragStartEvent) => {
    const item = event.active.data.current as OETDragItem | undefined;
    if (!item) return;
    setDragState({ activeItem: item, overId: null });
    if (item.type === DndTypes.COLUMN) {
      // `item.index` is the column's index into the full `oet.columns` array
      // (OETColumn.index reads its parent array), so resolve its id from there.
      const col = model!.uimodel.oet.columns[item.index];
      ui.setDraggedColumn(col ? col.id : null);
    }
  };

  // onDragOver: track the droppable under the pointer so each target can paint
  // its own hover highlight (`isOver`).
  const onDragOver = (event: DragOverEvent) => {
    setDragState((prev) => ({ ...prev, overId: event.over ? String(event.over.id) : null }));
  };

  const clearDrag = () => {
    setDragState({ activeItem: null, overId: null });
    ui.setDraggedColumn(null);
  };

  // onDragEnd: re-derive canDrop + the concrete move from the drop target's
  // data, exactly as the react-dnd `drop` specs did, then call the same ui
  // store actions. Anything not over a valid target is a no-op.
  const onDragEnd = (event: DragEndEvent) => {
    const item = event.active.data.current as OETDragItem | undefined;
    const over = event.over;
    if (item && over) {
      const data = over.data.current as Record<string, unknown> | undefined;
      const kind = data?.kind;
      if (item.type === DndTypes.COLUMN) {
        if (kind === 'column-cell') {
          const columnIndex = data!.columnIndex as number;
          if (canDropColumnOnCell(item, columnIndex)) {
            moveColumn(item.index, columnIndex + 1);
          }
        } else if (kind === 'column-first') {
          if (canDropColumnOnFirst(item)) {
            moveColumn(item.index, 0);
          }
        }
      } else if (item.type === DndTypes.ROW) {
        if (kind === 'row') {
          const rowEventId = data!.rowEventId as string;
          const rowGroup = data!.rowGroup as string;
          const rowIndex = data!.rowIndex as number;
          if (canDropRowOnRow(item, rowEventId, rowGroup, rowIndex)) {
            // Dropping on the parent row moves to the top of its sub-rows.
            const to = rowEventId === item.group ? 0 : rowIndex + 1;
            moveRow(item.index, to, item.group);
          }
        } else if (kind === 'row-header') {
          if (canDropRowOnHeader(item)) {
            moveRow(item.index, 0, item.group);
          }
        }
      }
    }
    clearDrag();
  };

  const renderContent = () => {
    const activeTool = ui.activeTool;
    const oet = model!.uimodel.oet;
    const selectable = activeTool === null || activeTool === 'delete';
    const popovers = isTouch ? activeTool === null : activeTool !== 'add_method';
    // Skip orphan OET uimodel entries whose model reference is dead: a column
    // whose `object` (object type) or a row whose `event` (event type) was
    // deleted out-of-band leaves the persisted uimodel pointing at an id absent
    // from the metamodel, so the `modelReference` resolver returns `undefined`.
    // Without this guard the header/body deref `col.object.id` / `r.event.id`
    // and the route-level error boundary takes down the whole project view.
    const columns = oet.columns.filter((c) => c.object != null);
    const rows = oet.flattenedRows.filter((r) => r.event != null);

    // Resolve the dragged row/column for the <DragOverlay> ghost. The active
    // item carries only `index`/`group`; resolve it back to the live node so the
    // overlay reproduces the real header/cells (mirroring the HTML5 drag-image).
    const active = dragState.activeItem;
    let overlayRow: IOETRow | null = null;
    let overlayColumn: IOETColumn | null = null;
    if (active && active.type === DndTypes.ROW) {
      if (active.group === 'top') {
        overlayRow = (oet.rows[active.index] as unknown as IOETRow) ?? null;
      } else {
        const parent = oet.flattenedRows.find((r) => r.event != null && r.event.id === active.group);
        overlayRow = parent ? ((parent.subRows[active.index] as unknown as IOETRow) ?? null) : null;
      }
    } else if (active && active.type === DndTypes.COLUMN) {
      overlayColumn = (oet.columns[active.index] as unknown as IOETColumn) ?? null;
    }

    return (
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={clearDrag}
      >
        <OETDragContext.Provider value={dragState}>
          <div
            ref={tableRef}
            className={`oet-content pb-[50px] ${activeTool === 'delete' ? 'active-delete' : ''}`}
          >
            <table className="oet-table">
              <OETHeader
                columns={columns}
                selectable={selectable}
                onSelect={onSelect}
                selected={selected}
              />
              <OETBody
                rows={rows}
                columns={columns}
                selectable={selectable}
                onSelect={onSelect}
                onRowHeaderClick={onRowHeaderClick}
                onCellClick={onCellClick}
                selected={selected}
                highlightedMethods={ui.highlightedMethods}
                popovers={popovers}
                draggedColumn={ui.draggedColumn}
              />
            </table>
          </div>
        </OETDragContext.Provider>
        <DragOverlay>
          {active ? (
            <OETDragOverlay
              row={overlayRow}
              column={overlayColumn}
              columns={columns}
              highlightedMethods={ui.highlightedMethods}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  };

  return (
    <Panel
      className="oet-view grow w-[calc(50%-300px)] min-w-[300px] flex flex-col"
      type="main"
      header={renderHeader}
    >
      {renderContent()}
    </Panel>
  );
});

const WrappedOET = (props: Omit<OETProps, 'setCapture'>) => {
  const imageContext = useContext(ImageContext);
  return <OET setCapture={(fn) => imageContext.setCaptureOET(fn)} {...props} />;
};

/**
 * The props supplied to the exported OET by its parent (ProjectVersion). The
 * `isTouch` / export callbacks are injected from the store below, so the parent
 * supplies the rest plus the table `id` (carried through, never read here).
 */
type InjectedOETProps = Omit<
  OETProps,
  'setCapture' | 'isTouch' | 'onExportImage' | 'onExportCsv'
> & {
  /** The model version id (passed through by the parent, not read here). */
  id?: string;
};

// dnd-kit's <DndContext> lives inside the OET (around the table) and needs no
// ref on this wrapper, so the react-dnd-era forwardRef ref-absorber is gone:
// this is a plain function component that injects the store-derived props.
const InjectedOET = function InjectedOET(props: InjectedOETProps) {
  const { ui } = useStores();
  return (
    <WrappedOET
      isTouch={ui.isTouch}
      onExportImage={() => ui.project.setExportOETImage(true)}
      onExportCsv={() => ui.project.setExportOETCsv(true)}
      {...props}
    />
  );
};

export default InjectedOET;
